import { useState } from 'react';
import { useForm } from 'react-hook-form';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Header from '../components/header';
import SaveButton from '../components/saveButton';
import UserForm from '../components/userform';
import OtpDialog from '../components/otpdialog';
import ExternalLogin from '../components/externallogin';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import moment from 'moment-timezone';

function SignupBetaForm({ externalClaims }) {
	const formSchema = Yup.object().shape({
		password: Yup.string()
			.required('Password is mandatory')
			.matches(
				/^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,}$/gi,
				'Min 8 characters, 1 letter, 1 number and 1 special character'
			),
		confirmPassword: Yup.string()
			.required('Confirm Password is mandatory')
			.oneOf([Yup.ref('password')], 'Passwords does not match'),
		email: Yup.string()
			.email('Email is mandatory')
			.required('Email is mandatory'),
		firstName: Yup.string()
			.required('First name is mandatory')
			.min(2, 'First name must be at 2 char long'),
		lastName: Yup.string()
			.required('Last name is mandatory')
			.min(2, 'Last name must be at 2 char long'),
		company: Yup.string(),
		position: Yup.string(),
		workUnit: Yup.string()
			.required('Work Unit is mandatory'),
		employeeId: Yup.string(),
		mobileNumber: Yup.string()
			.required('Mobile number is mandatory')
			.matches(
			/\+\d{10}/,
			'Enter mobile with country code (e.g. +614..)'
		),
	});

	const [error, setError] = useState('');
	const [saving, setSaving] = useState(false);
	const {
		register,
		handleSubmit,
		control,
		getValues,
		setValue,
		formState: { errors, isValid },
	} = useForm({
		mode: 'onChange',
		resolver: yupResolver(formSchema),
		defaultValues: {
			...externalClaims,
			userTimezone: moment.tz.guess(),
		},
	});

	const query = new URLSearchParams(window.location.search);
	const returnUrl = query.get('ReturnUrl') || '/home';
	const betaProduct = query.get('betaProduct');
	const product = query.get('product');

	const navigate = useNavigate();

	const [isOtpOpen, setOtpIsOpen] = useState(false);
	const onEnterOtp = (otp: string) => {
		const formD = getValues();
		onLoginSubmit({
			...formD,
			otpCode: otp,
		});
	};

	const onIgnoreOtp = () => {
		const formD = getValues();
		onLoginSubmit({
			...formD,
			otpCode: '',
			skipOtpVerification: true,
		});
	};

	const onResendOtp = () => {
		setSaving(true);
		setError('');
		setOtpIsOpen(false);
		const formD = getValues();
		axios
			.post(`/api/auth/ResendOtp`, formD)
			.then((response: any) => {
				setSaving(false);
				setOtpIsOpen(true);
			})
			.catch((error) => {
				setSaving(false);
				setOtpIsOpen(true);
				setError('Error occured. Unable to send OTP');
			});
	};

	const onLoginSubmit = (data: any) => {
		const toSend = {
			...data,
			betaProduct,
			product,
			returnUrl,
		};
		// console.log(
		// 	'🚀 ~ file: SignupBeta.tsx:118 ~ onLoginSubmit ~ toSend:',
		// 	toSend
		// );
		// return;

		setSaving(true);
		setOtpIsOpen(false);
		axios
			.post(`/api/auth/signup`, toSend)
			.then((response: any) => {
				setSaving(false);
				if (response.data.requireOtp) {
					setOtpIsOpen(true);
				} else {
					setOtpIsOpen(false);
					if (response.data.requiredConfirmation) {
						setError('');
						navigate('/confirmemail', { replace: false });
					} else {
						window.location.href = response.data.returnUrl;
					}
				}
			})
			.catch((error) => {
				setSaving(false);
				if (error?.response?.data?.otpCode) {
					setError(error?.response?.data?.otpCode[0]);
					setOtpIsOpen(true);
					return;
				}
				if (error?.response?.data?.email) {
					setError(error?.response?.data?.email[0]);
					return;
				}
				if (error?.response?.data?.password) {
					setError(error?.response?.data?.password[0]);
					return;
				}
				setError('Error occured. Unable to register user');
			});
	};

	return (
		<div className='min-h-screen flex flex-col items-center justify-center bg-gray-50'>
			<OtpDialog
				isOpen={isOtpOpen}
				isLoading={saving}
				error={error}
				closeModal={() => setOtpIsOpen(false)}
				onEnterOtp={onEnterOtp}
				onIgnoreOtp={onIgnoreOtp}
				resendOtp={onResendOtp}
			></OtpDialog>

			<Header />

			<div className='font-bold text-2xl py-8 text-center'>
				Thanks for signing up for {query.get('betaProduct')} Beta
				<div className='font-bold text-3xl py-8'>
					Complete your profile
				</div>
			</div>

			<div className='flex flex-col bg-white shadow-md px-4 sm:px-6 md:px-8 lg:px-10 py-8 mb-16 rounded-md w-full max-w-xl'>
				<div className='mt-2'>
					<ExternalLogin action='Continue'></ExternalLogin>
					<div className='relative mt-10 h-px bg-gray-300'>
						<div className='absolute left-0 top-0 flex justify-center w-full -mt-3'>
							<span className='bg-white px-4 text-md text-gray-500 '>
								OR
							</span>
						</div>
					</div>

					<form
						onSubmit={handleSubmit(onLoginSubmit)}
						className='pt-10 pb-6'
					>
						<UserForm
							control={control}
							errors={errors}
							emailReadonly={true}
							showPassword={true}
							error={error}
							register={register}
							showTimezone={false}
							setValue={setValue}
						/>

						<div className='flex w-full mt-10'>
							<SaveButton
								disabled={!isValid}
								saving={saving}
								type='submit'
								className='flex items-center text-white justify-center focus:outline-none rounded-lg py-4 w-full bg-blue-600 hover:bg-blue-700'
							>
								Complete Registration
							</SaveButton>
						</div>
					</form>
				</div>
			</div>
		</div>
	);
}

function SignupBeta() {
	const query = new URLSearchParams(window.location.search);
	return (
		<SignupBetaForm
			externalClaims={{
				firstName: query.get('firstName'),
				lastName: query.get('lastName'),
				email: query.get('email'),
				betaProduct: query.get('betaProduct'),
			}}
		/>
	);
}
export default SignupBeta;